/* eslint-disable unused-imports/no-unused-vars */
'use client'

import { useSearchParams } from 'next/navigation'
import { ChangeEvent, useCallback, useState } from 'react'

import { SortingState } from '@tanstack/react-table'

import { DataIndukVariant } from '@/apis/types'

import { PTK_MAP_SORT_PARAM_TO_COLUMN } from '../components/CustomTable/PtkTable/columns'
import { SATPEN_MAP_SORT_PARAM_TO_COLUMN } from '../components/CustomTable/SatpenTable/columns'
import { YAYASAN_MAP_SORT_PARAM_TO_COLUMN } from '../components/CustomTable/YayasanTable/columns'
import { VARIANT } from '../constant'

export const generateSearchParam = ({
  customKeyword,
  customSortBy,
  customSortDir,
  customJenis,
  jalur,
  bentuk,
  status,
  jenis,
  gender,
  keyword,
  sortBy,
  sortDir,
}: {
  jalur: string
  bentuk: string
  status: string
  jenis: string
  gender: string
  keyword: string
  sortBy: string
  sortDir: string
  customKeyword?: string
  customSortBy?: string
  customSortDir?: string
  customJenis?: string
}) => {
  const searchParams = new URLSearchParams()
  if (jalur) {
    searchParams.set('jalur', jalur)
  }
  if (bentuk) {
    searchParams.set('bentuk', bentuk)
  }
  if (status) {
    searchParams.set('status', status)
  }

  // Support custom jenis
  if (typeof customJenis !== 'undefined') {
    if (customJenis !== '') {
      searchParams.set('jenis', customJenis)
    }
  } else if (jenis) {
    if (jenis !== '') {
      searchParams.set('jenis', jenis)
    }
  }

  if (gender) {
    searchParams.set('gender', gender)
  }

  // Support custom keyword
  if (typeof customKeyword !== 'undefined') {
    if (customKeyword !== '') {
      searchParams.set('s', customKeyword)
    }
  } else if (keyword) {
    if (keyword !== '') {
      searchParams.set('s', keyword)
    }
  }

  // Support custom sort by
  if (typeof customSortBy !== 'undefined') {
    if (customSortBy !== '') {
      searchParams.set('sortBy', customSortBy)
    }
  } else if (sortBy) {
    searchParams.set('sortBy', sortBy)
  }

  // Support custom sort dir
  if (typeof customSortDir !== 'undefined') {
    if (customSortDir !== '') {
      searchParams.set('sortDir', customSortDir)
    }
  } else if (sortBy) {
    searchParams.set('sortDir', sortDir)
  }

  const str = searchParams.toString()
  return str ? `?${str}` : '?'
}

export const useFilterState = ({
  variant = VARIANT.SATPEN,
  view,
}: {
  variant?: DataIndukVariant
  view: 'table' | 'list'
}) => {
  const isYayasan = variant === VARIANT.YAYASAN
  const isPTK = variant === VARIANT.PTK
  const isTableView = view === 'table'
  const searchParams = useSearchParams()

  const queryJalur = (searchParams?.get?.('jalur') as string) || ''
  const queryBentuk = (searchParams?.get?.('bentuk') as string) || ''
  const queryStatus = (searchParams?.get?.('status') as string) || ''
  const queryJenis = (searchParams?.get?.('jenis') as string) || ''
  const queryKeyword = (searchParams?.get?.('s') as string) || ''
  const queryGender = (searchParams?.get?.('gender') as string) || ''

  const querySortBy = (searchParams?.get?.('sortBy') as string) || ''
  const querySortDir = (searchParams?.get?.('sortDir') as string) || ''

  const [keyword, setKeyword] = useState<string>(queryKeyword)
  const [sortBy, setSortBy] = useState<string>(querySortBy)
  const [sortDir, setSortDir] = useState<string>(querySortDir)

  const [sortingState, setSortingState] = useState<SortingState>(
    [
      querySortBy
        ? {
            id: isYayasan
              ? YAYASAN_MAP_SORT_PARAM_TO_COLUMN[querySortBy] || querySortBy
              : isPTK
                ? PTK_MAP_SORT_PARAM_TO_COLUMN[querySortBy] || querySortBy
                : SATPEN_MAP_SORT_PARAM_TO_COLUMN[querySortBy] || querySortBy,
            desc: querySortDir === 'desc',
          }
        : null,
    ].filter(Boolean) as SortingState
  )

  const [selectedJalur, setSelectedJalur] = useState<string>(queryJalur)
  const [selectedBentuk, setSelectedBentuk] = useState<string>(queryBentuk)
  const [selectedStatus, setSelectedStatus] = useState<string>(queryStatus)
  const [selectedJenis, setSelectedJenis] = useState<string>(queryJenis)
  const [selectedGender, setSelectedGender] = useState<string>(queryGender)

  const handleChangeJalur = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    setSelectedJalur(newValue)
  }

  const handleChangeStatus = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    setSelectedStatus(newValue)
  }

  const handleChangeJenis = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    setSelectedJenis(newValue)
  }

  const handleChangeGender = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    setSelectedGender(newValue)
  }

  const handleChangeBentuk = (newValue: string) => {
    setSelectedBentuk(newValue)
  }

  const handleChangeSortBy = (newValue: string) => {
    setSortBy(newValue)
  }

  const handleChangeSortDir = (newValue: string) => {
    setSortDir(newValue)
  }

  const handleChangeSortingState = (newValue: SortingState) => {
    setSortingState(newValue)
  }

  const handleSetKeyword = (newValue: string) => {
    setKeyword(newValue)
  }

  const getFilterSearchParams = useCallback(
    ({
      customKeyword,
      customSortBy,
      customSortDir,
      customJenis,
    }: {
      customKeyword?: string
      customSortBy?: string
      customSortDir?: string
      customJenis?: string
    }) => {
      return generateSearchParam({
        jalur: selectedJalur,
        bentuk: selectedBentuk,
        status: selectedStatus,
        jenis: selectedJenis,
        gender: selectedGender,
        keyword,
        sortBy,
        sortDir,
        customKeyword,
        customSortBy,
        customSortDir,
        customJenis,
      })
    },
    [
      selectedJalur,
      selectedBentuk,
      selectedStatus,
      selectedJenis,
      selectedGender,
      keyword,
      sortBy,
      sortDir,
    ]
  )

  const getFilterSearchParamsFromQuery = useCallback(() => {
    return generateSearchParam({
      jalur: queryJalur,
      bentuk: queryBentuk,
      status: queryStatus,
      jenis: queryJenis,
      gender: queryGender,
      keyword: queryKeyword,
      sortBy: querySortBy,
      sortDir: querySortDir,
    })
  }, [
    queryJalur,
    queryBentuk,
    queryStatus,
    queryJenis,
    queryGender,
    queryKeyword,
    querySortBy,
    querySortDir,
  ])

  return {
    // -- Query params
    queryJalur,
    queryBentuk,
    queryStatus,
    queryJenis,
    queryKeyword,
    querySortBy,
    querySortDir,
    queryGender,

    // -- Filters
    selectedJalur,
    selectedBentuk,
    selectedStatus,
    selectedJenis,
    selectedGender,
    keyword,
    sortBy,
    sortDir,
    sortingState,

    // -- Change handlers
    handleChangeJalur,
    handleChangeBentuk,
    handleChangeStatus,
    handleChangeJenis,
    handleSetKeyword,
    handleChangeSortBy,
    handleChangeSortDir,
    handleChangeSortingState,
    handleChangeGender,

    // -- Get search params for updating state
    getFilterSearchParams,
    getFilterSearchParamsFromQuery,
  }
}
